import { RequireAtLeastOne } from '../../../@core/api/models';
import { InitiativesService } from '../../../@core/utils';
import { PerimetersService } from '../../../@core/utils/perimeters.service';
import { AssetHistoryDialogComponent } from '../asset-history-dialog/asset-history-dialog.component';
import { NbDialogService, NbCardModule, NbButtonModule, NbListModule, NbTooltipModule, NbSpinnerModule } from '@nebular/theme';
import { ScopeService } from '../../../@core/utils/scope.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { combineLatest, of, Subscription } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { KPI, APIService } from '../../../@core/api';
import {
  AssetHistoryService,
  AssetsChangesService,
  AssetsService,
  AuditExpertService,
  ConfigService,
  DocumentsService,
  I18nService,
  InvestmentsService,
  StateLevelsService,
  UsersService,
  UtilsService,
  SettingsService,
} from '../../../@core/utils';
import { EnvironmentalProfileService } from '../../../@core/utils/environmental-profile.service';
import { AgGridDocumentsComponent, EnvironmentalProfilesDialogComponent, SheetDialogComponent } from '..';
import { InvestmentListDrawerComponent } from '../investment-list-drawer/investment-list-drawer.component';
import { AssetQuantityPipe } from '../../pipes/asset-quantity.pipe';
import { AssetLocationPipe } from '../../pipes/asset-location.pipe';
import { DefaultsListComponent } from '../defaults-list/defaults-list.component';
import { LinkedAssetsWithPicturesComponent } from '../linked-assets-with-pictures/linked-assets-with-pictures.component';
import { AssetProjectsTimelineComponent } from '../charts/asset-projects-timeline/asset-projects-timeline.component';
import { LifecycleChartComponent } from '../charts/lifecycle-chart/lifecycle-chart.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { NgStyle, DecimalPipe, DatePipe, AsyncPipe } from '@angular/common';
import { LightgalleryModule } from 'lightgallery/angular';
import { LightGallery } from 'lightgallery/lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import { LightGalleryCoreSettings } from 'lightgallery/lg-settings';
import { LIGHT_GALLERY_LICENSE_KEY } from '../../../../license-keys';
import { AssetFormDrawerComponent } from '../asset-form-drawer/asset-form-drawer.component';
import { MappedInvestmentDocuments } from '../../../@core/utils/investments.service';
import { SaveToastService } from '../../../@core/utils/save-toast.service';

@Component({
  selector: 'app-asset-sheet',
  templateUrl: './asset-sheet.component.html',
  styleUrls: ['./asset-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    
    BreadcrumbsComponent,
    NbCardModule,
    NgStyle,
    NbButtonModule,
    NbListModule,
    NbTooltipModule,
    InvestmentListDrawerComponent,
    FontAwesomeModule,
    LifecycleChartComponent,
    AssetProjectsTimelineComponent,
    LinkedAssetsWithPicturesComponent,
    DefaultsListComponent,
    DecimalPipe,
    TranslateModule,
    AssetLocationPipe,
    AssetQuantityPipe,
    LightgalleryModule,
    DatePipe,
    AsyncPipe,
    NbSpinnerModule,
    forwardRef(() => AssetFormDrawerComponent)
  ],
})
export class AssetSheetComponent implements OnDestroy, OnInit {

  @Input() public assetId: number;
  @Input() public minimizedView: boolean;

  public asset: KPI.MappedAsset;
  public futureAsset: KPI.MappedAsset;
  public previousAsset: KPI.MappedAsset;
  public replacementInvestment: KPI.MappedInvestment;
  public superCategory: KPI.SuperCategory;
  public category: KPI.Category;
  public subCategory: KPI.SubCategory;
  public assetType: KPI.AssetType;
  public showInvestmentsDrawer: boolean = false;
  public pictures: KPI.AssetPicture[] = [];
  public investments: KPI.MappedInvestment[] = [];
  public linkedAssets: KPI.MappedAsset[] = [];
  public hasInvWithDurationDeviation: boolean = false;
  public environmentalProfiles: KPI.EnvironmentalProfile[] = [];
  public relevantEnvironmentalProfile: KPI.EnvironmentalProfile = null;
  // public tasks: KPI.MappedTask[];
  private totalLifetime: number;
  private lifetimeVsTheorical: number = null;
  public lifetimeVsTheoricalPercent: number = null;
  public emissionsExcess: string = null;
  public isCycleLifeDisplay: boolean = true;
  public lifeCycleTabTitle: string = '';
  public invTabTitle: string = '';
  // public taskTabTitle: string = '';
  public initiativesTabTitle: string = '';
  public brandModelPowerValue: string = '';
  public installationInfo: string = '';
  private subscriptions: Subscription[] = [];
  public note: number = null;
  public isSourceAuditor: boolean;
  public defaults: KPI.Default[] = [];
  public docCount: number;
  public initiativesArray: KPI.MappedInitiative[] = [];
  public technicalStateLabel: string = '';
  public iaGtpMockMessage: string = '';
  public showIaGtpCard: boolean = false;
  public iaGtpLoading: boolean = false;
  public currentYear = new Date().getFullYear();
  public pageRefreshed: boolean = true;
  public energyEnabled: boolean = false;
  public lastNotation: KPI.AuditChartPoint;
  public visible: boolean = true;
  public galleryId: number = UtilsService.makeRandomId();
  public initiativesTabActive: boolean = false;
  public readOnly: boolean = false;
  /* Price sheets */
  public priceSheets: KPI.PriceSheet[] = [];
  public seeMore: boolean = false;
  public lastModificationDate: string = null;
  public lastModificationUser: string = null;
  /* Customer config */
  public customerConfig: KPI.CustomerConfig = null;
  /* Asset form drawer */
  public showAssetFormDrawer: boolean = false;
  /* OPEX history */
  public opexItems: KPI.AssetOpex[] = [];
  /**
   * Image gallery settings
   */
  public settings: RequireAtLeastOne<LightGalleryCoreSettings> = {
    counter: false,
    speed: 500,
    plugins: [lgZoom, lgThumbnail],
    dynamic: true,
    dynamicEl: [],
    licenseKey: LIGHT_GALLERY_LICENSE_KEY,
    zoomFromOrigin: true,
  };

  private lightGallery!: LightGallery;

  public onInit = (detail): void => {
    this.lightGallery = detail.instance;
  };

  public openGallery = () => {
    this.lightGallery.openGallery();
  };
  public endOfLifeYear: number = 0;

  constructor(
    private assetsService: AssetsService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private stateLevelService: StateLevelsService,
    private auditExpertService: AuditExpertService,
    private scopeService: ScopeService,
    private router: Router,
    private dialogService: NbDialogService,
    private environmentalProfileService: EnvironmentalProfileService,
    private utilsService: UtilsService,
    private perimetersService: PerimetersService,
    private assetsChangesService: AssetsChangesService,
    public i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private initiativesService: InitiativesService,
    private investmentsService: InvestmentsService,
    private documentsService: DocumentsService,
    private assetHistoryService: AssetHistoryService,
    private usersService: UsersService,
    private configService: ConfigService,
    private settingsService: SettingsService,
    private api: APIService,
    private saveToast: SaveToastService,
  ) { }

  public opexManagementEnabled$ = this.settingsService.opexManagement$;

  public ngOnInit(): void {
    this.subscriptions.push(
      this.scopeService.user$.subscribe((user) => this.energyEnabled = user.can_access_energy),
      this.configService.customerConfig$.subscribe((config) => {
        this.customerConfig = config;
      }),
      this.route.paramMap.pipe(
        switchMap((paramMap) => {
          const assetId = this.assetId && this.minimizedView
            ? this.assetId
            : paramMap.get('id')
              ? Number(paramMap.get('id'))
              : null;

          if (assetId) {
            return combineLatest([
              this.assetsChangesService.pendingChangesNumber$.pipe(
                filter((pendingChangesNumber) => !pendingChangesNumber)
              ),
              this.assetsService.refresh$
            ]).pipe(
              switchMap(() =>
                combineLatest([
                  this.assetsService.getAsset(assetId),
                  this.auditExpertService.getAssetKPIExpert(assetId),
                  this.perimetersService.perimeters$,
                  this.assetHistoryService.loadHistoryForAsset(assetId),
                  this.usersService.allUsers$.pipe(first()),
                  this.usersService.currentUserPerimeterPermissions$,
                ]),
              ),
            );
          } else {
            return of([]);
          }
        }),
      ).subscribe(([asset, defaults, perimeters, history, users, permissions]) => {

        const relevantPerimeter = asset.perimeters.find((p) => !!p.level_parent === false)
        if (relevantPerimeter) {
          const relevantPerimeterPerm = permissions.find((perm) => perm.perimeter === relevantPerimeter.id)
          this.readOnly = relevantPerimeterPerm?.read_only || !this.customerConfig?.is_show_edit
        }
        this.defaults = defaults;
        // SSO : Be careful because the text "1_global_site" & "etage_non_defini"
        // can very easily be changed by people outside the technical team
        // (they come from the YML files that are used to define the structure of the platform)
        // leading to changes to the displayed information, new user stories & lost time...
        this.asset = asset;
        const historyArr = history.history_items
        if (historyArr.length) {
          const lastModification = historyArr[historyArr.length - 1];
          this.lastModificationDate = lastModification?.date;
          this.lastModificationUser = users.find((user) => user.get_user_id === lastModification?.user)?.name;
        }

        // If the asset's site doesn't match the selected perimeters, redirect the user to home
        const site = this.asset?.perimeters.find((p) => !p.is_monosite);

        if (this.asset && site && perimeters.find((p) => p.id === site.id)) {

          this.assetType = this.asset.asset_type_object;
          this.subCategory = this.asset.sub_category;
          this.category = this.asset.category;
          this.superCategory = this.asset.super_category;

          this.brandModelPowerValue = '';
          if (this.asset.brand) {
            this.brandModelPowerValue += this.asset.brand;
          }
          if (this.asset.model) {
            if (this.brandModelPowerValue !== '') {
              this.brandModelPowerValue += ' / ';
            }
            this.brandModelPowerValue += this.asset.model;
          }
          if (this.asset.power) {
            if (this.brandModelPowerValue !== '') {
              this.brandModelPowerValue += ' / ';
            }
            this.brandModelPowerValue += this.asset.power;
          }
          if (this.brandModelPowerValue === '') {
            this.brandModelPowerValue = this.translate.instant(
              'pages.insight.asset.no-value',
            );
          }

          this.subscriptions.push(
            this.assetsService.getAssetLifecycle(this.asset.id).subscribe((lifecycle) => {
              this.totalLifetime = lifecycle.planned_eol - lifecycle.installation_year;
              this.lastNotation = lifecycle.audit_points[lifecycle.audit_points.length - 1];
              this.note = this.lastNotation?.y;
              this.technicalStateLabel = this.stateLevelService.getTechnicalStateLabelFromNote(this.note);
              this.showIaGtpCard = this.note !== null && this.note !== undefined && lifecycle?.consistency_check?.is_consistent === false;
              this.iaGtpMockMessage = '';
              if (this.showIaGtpCard) {
                this.iaGtpLoading = true;
                setTimeout(() => {
                  this.iaGtpMockMessage = this.getIaGtpMockMessage(lifecycle);
                  this.iaGtpLoading = false;
                  this.cdRef.detectChanges();
                }, 1500);
              }
              this.lifetimeVsTheoricalPercent = this.computeLifetimeDifference(
                this.totalLifetime,
                this.asset.asset_type_object?.expected_duration,
              );
              this.lifetimeVsTheorical = this.totalLifetime - this.asset.asset_type_object?.expected_duration;
              this.isSourceAuditor = !lifecycle.consistency_check.is_source_model;
              this.loadEnvironmentalProfiles();
              this.cdRef.detectChanges();
            }),
          );

          // Get the linked assets (parent and children)
          this.linkedAssets = [];
          if (this.asset.children && this.asset.children.length) {
            this.linkedAssets.push(...this.asset.children);
          }
          if (this.asset.parent) {
            this.subscriptions.push(
              this.assetsService.getAsset(this.asset.parent)
                .subscribe((parentAsset) => {
                  if (parentAsset) {
                    this.linkedAssets.push(parentAsset);
                    this.cdRef.detectChanges();
                  }
                }),
            );
          }

          // Get the pictures of the asset and its linked assets
          this.subscriptions.push(
            this.assetsService.getAssetsPictures([
              this.asset.id,
              ...this.linkedAssets.map((a) => a.id),
            ]).subscribe((pictures) => {
              this.settings.dynamicEl = pictures.filter((p) => p.asset === this.asset.id).map((p) => ({
                id: p.id,
                src: p.picture,
                thumb: p.thumbnail,
              }));

              this.linkedAssets = this.linkedAssets.map((a) => ({
                ...a,
                pictures: pictures.filter((p) => p.asset === a.id),
              }));

              this.cdRef.detectChanges();
            }),
          )

          if (this.asset.replacement_of && this.asset.installation_year >= this.currentYear) {
            this.note = 100;
            this.technicalStateLabel = this.stateLevelService.getTechnicalStateLabelFromNote(this.note);
          }

          if (this.asset.initiatives?.length) {
            this.initiativesArray = [];
            this.asset.initiatives.map((initiative) => {
              this.initiativesService.getInitiative(
                initiative.id
              ).subscribe((initiative) => {
                this.initiativesArray.push(initiative);
                this.cdRef.detectChanges();
              });
            });
          }
          this.loadInvestments();
          this.loadOpex();
          this.loadPreviousAsset();
          this.loadFutureAsset();
          this.getDocumentsCount();
          // this.loadTasks();
          this.cdRef.detectChanges();
        } else {
          this.router.navigate(['insight', 'technical-state']);
        }

      }),
      this.translate.get('pages.insight.asset.lifecycle').subscribe((lifeCycleTabTitle) => {
        this.lifeCycleTabTitle = lifeCycleTabTitle;
        this.cdRef.detectChanges();
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Load the investments linked to the asset
   */
  private loadInvestments(): void {
    this.subscriptions.push(
      this.investmentsService.loadInvestmentWithParams({ assets: this.asset.id }).subscribe((investments) => {
        this.investments = investments as MappedInvestmentDocuments[];
        if (this.investments.length) {
          this.endOfLifeYear = 0;
          this.investments.forEach((investment) => {
            if (!investment.has_no_impact && !investment.investment_type_object.replacement && this.isPlanned(investment)) {
              const endOfLifeAfterInvest = investment.final_schedule + investment.lifetime_after_investment;
              if (endOfLifeAfterInvest > this.endOfLifeYear) {
                this.endOfLifeYear = endOfLifeAfterInvest;
              }
            }
          })
        } else {
          this.endOfLifeYear = this.asset.end_of_life;
        }
        this.cdRef.detectChanges();
      }),
    );
  }

  private getIaGtpMockMessage(lifecycle: KPI.AssetLifecycle): string {
    const consistency = lifecycle?.consistency_check;
    // Backend returns total lifetime; convert to remaining so the message matches
    // what the audit app displays (durée de vie restante).
    const age = this.currentYear - lifecycle.installation_year;
    const params = {
      minSuggestedLifetime: Math.max(0, (consistency?.min_suggested_lifetime ?? 8) - age),
      maxSuggestedLifetime: Math.max(0, (consistency?.max_suggested_lifetime ?? 11) - age),
    };

    const relatedInvestmentsCount = (lifecycle?.planned_investments_points?.length || 0)
      + (lifecycle?.hypothesis_investments_points?.length || 0);

    if (relatedInvestmentsCount === 0) {
      return this.translate.instant(_('pages.common.charts.lifecycle-chart.iagtp_inconsistent_message_no_investment'), params);
    }

    if (relatedInvestmentsCount === 1) {
      return this.translate.instant(_('pages.common.charts.lifecycle-chart.iagtp_inconsistent_message_single_investment'), params);
    }

    return this.translate.instant(_('pages.common.charts.lifecycle-chart.iagtp_inconsistent_message_multiple_investments'), params);
  }

  private loadOpex(): void {
    this.subscriptions.push(
      this.api.assets_opex({ asset: this.asset.id }).subscribe((items) => {
        this.opexItems = items;
        this.cdRef.detectChanges();
      }),
    );
  }

  public get opexYearRange(): string {
    if (!this.opexItems.length) return '';
    const years = this.opexItems.map((o) => o.year);
    const min = Math.min(...years);
    const max = Math.max(...years);
    return min === max ? `${min}` : `${min} - ${max}`;
  }

  public formatOpexAmount(value: number): string {
    return this.i18n.formatPriceWithThousands(value);
  }

  private isPlanned(investment: KPI.MappedInvestment): boolean {
    return !investment.status_object.is_closed && !investment.status_object.hypothesis
  }

  private loadFutureAsset(): void {
    if (this.asset.replaced_by) {
      this.subscriptions.push(
        this.assetsService.getAsset(this.asset.replaced_by).subscribe((future_asset) => {
          this.futureAsset = future_asset;
          this.assetsService.getAssetsPictures([this.futureAsset.id]).subscribe((assetPictures) => {
            Object.assign(this.futureAsset, { pictures: assetPictures });
          });
          this.cdRef.detectChanges();
        }),
      );
    } else {
      this.futureAsset = null;
      this.cdRef.detectChanges();
    }
  }

  /**
   * Load previous asset if any
   */
  private loadPreviousAsset(): void {
    if (this.asset.replacement_of) {
      this.subscriptions.push(
        this.assetsService.getAsset(this.asset.replacement_of)
          .subscribe((previous_asset) => {
            this.previousAsset = previous_asset;
            this.assetsService.getAssetsPictures([this.previousAsset.id]).subscribe((assetPictures) => {
              Object.assign(this.previousAsset, { pictures: assetPictures });
            });
            this.linkedAssets.push(previous_asset);
            this.cdRef.detectChanges();
          }),
      )
    }
  }

  /**
   * Load the tasks linked to the asset
   */
  /*private loadTasks(): void {
    this.tasksService.getAssetTasks(this.asset).subscribe((tasks) => {
      this.tasks = tasks;
      this.translate
        .get('pages.insight.asset.tasks', { nbTasks: this.tasks.length })
        .subscribe((taskTabTitle) => {
          this.taskTabTitle = taskTabTitle;
        });
    });
  }*/

  public openHistory(): void {
    this.dialogService.open(AssetHistoryDialogComponent, {
      context: {
        assetId: this.asset.id
      }
    })
  }

  private loadEnvironmentalProfiles(): void {
    if (this.asset?.asset_type) {
      this.subscriptions.push(
        this.environmentalProfileService.getEnvironmentalProfilesByAssetType(this.asset.asset_type).subscribe(
          (environmentalProfiles) => {
            this.environmentalProfiles = environmentalProfiles;
            if (environmentalProfiles?.length && this.lifetimeVsTheorical < 0) {
              let relevantEnvironmentalProfile = environmentalProfiles.find((profile) =>
                profile.is_default,
              );

              if (!relevantEnvironmentalProfile) {
                relevantEnvironmentalProfile = environmentalProfiles[0];
              }

              const embodied_emissions = relevantEnvironmentalProfile.production_emissions
                + relevantEnvironmentalProfile.transport_emissions
                + relevantEnvironmentalProfile.maintenance_emissions
                + relevantEnvironmentalProfile.end_of_life_emissions;

              const yearly_embodied_emissions = this.asset.asset_type_object?.expected_duration
                ? Math.round(embodied_emissions / this.asset.asset_type_object.expected_duration)
                : 0;

              this.emissionsExcess = this.utilsService.formateNumber(
                Math.round(Math.abs(this.lifetimeVsTheorical) * yearly_embodied_emissions)
              );
            }
            this.cdRef.detectChanges();
          }
        )
      );
    }
  }

  private computeLifetimeDifference(totalLifetime: number, theoricalLifetime: number): number {
    if (totalLifetime && theoricalLifetime) {
      return Math.round((totalLifetime - theoricalLifetime) / theoricalLifetime * 100);
    } else {
      return 0;
    }
  }

  public openEnvironmentalProfiles(): void {
    this.dialogService.open(EnvironmentalProfilesDialogComponent, {
      context: {
        assetType: this.asset.asset_type_object,
        environmentalProfiles: this.environmentalProfiles,
      }
    });
    this.cdRef.detectChanges();
  }

  public openCostDialog(): void {
    this.showInvestmentsDrawer = true;
    this.cdRef.detectChanges();
  }

  public closeInvestmentsDrawer(): void {
    this.showInvestmentsDrawer = false;
    this.cdRef.detectChanges();
  }

  public getVisibilityValue(e): void {
    this.visible = e;
  }


  public updateAfterEditing(result: KPI.CellEditorResult) {
    this.assetsChangesService.addChange(
      result.id,
      result.field,
      result.value,
    );
  }

  /** Open a new tab to show the capex audit */
  public goToAudit() {
    if (!this.asset.perimeters || !this.asset.id) {
      return alert(this.translate.instant("Configuration error, Link to equipement is broken"))
    }
    const params = {
      asset: this.asset,
      siteId: this.asset.perimeters[0].id,
      dialogCtrl: this.dialogService
    }
    this.assetsService.openAuditAssetEditor(params)
  }

  public getTechnicalStateLabel(notation: number): string {
    return this.stateLevelService.getTechnicalStateLabelFromLevel(notation);
  }

  public getInvestBudget(investments: KPI.MappedInvestment[]): string {
    const totalBudget = investments.reduce((acc, investment) => {
      const budget = investment.slices
        .filter((s) => s.status === investment.status)
        .sort((a, b) => a.year - b.year)
        .reduce((sum, s) => sum + Number(s.price), 0);

      return acc + budget;
    }, 0);

    return this.i18n.formatCurrency(totalBudget);
  }

  public seeInvestments(): void {
    this.showInvestmentsDrawer = true;
    this.cdRef.detectChanges();
  }

  public onInvestmentSelected(investmentId: number): void {
    const context: any = {
      autoFocus: false,
      investmentId: investmentId,
    };

    this.dialogService.open(
      SheetDialogComponent,
      {
        context,
      }
    );
  }

  public getActionsSavings(actions: KPI.MinimalInitiative[]): number {
    let totalSavings = 0;
    actions.forEach((action) => {
      if (!!action?.gains) {
        totalSavings += action.gains.reduce((acc, gain) => acc + gain.theoretical_savings_full_year, 0);
      }
    });
    return totalSavings;
  }

  public seeActions(actions: KPI.MinimalInitiative[]): void {

    const context: any = {
      autoFocus: false,
    };

    if (actions.length > 1) {
      const actionIds = actions.map((action) => action.id);
      context['initiativesList'] = true;
      context['initiativesListFilters'] = {
        initiative_id: actionIds,
      }
    } else {
      context['initiativeId'] = actions[0].id
    }

    this.dialogService.open(
      SheetDialogComponent,
      {
        context,
      }
    );
  }

  private getDocumentsCount(): void {
    this.subscriptions.push(
      this.documentsService.getDocumentsCount(
        [this.asset.id],
        this.asset?.investments.length ? this.asset?.investments.map(inv => inv.id) : [],
        this.asset?.initiatives.length ? this.asset?.initiatives.map(ini => ini.id) : [],
        []
      ).subscribe((docCount) => {
        this.docCount = docCount;
      })
    )
  }

  public seeDocuments(): void {
    this.dialogService.open(
      AgGridDocumentsComponent,
      {
        autoFocus: false,
        context: {
          title: this.translate.instant('the asset'),
          assetIds: [this.asset.id],
          investIds: this.asset?.investments.length ? this.asset?.investments.map(inv => inv.id) : [],
          actionIds: this.asset?.initiatives.length ? this.asset?.initiatives.map(ini => ini.id) : [],
        },
      }
    );
  }

  public openAssetForm(): void {
    this.showAssetFormDrawer = true;
    this.cdRef.detectChanges();
  }

  public closeAssetFormDrawer(): void {
    this.showAssetFormDrawer = false;
    if (this.asset?.id) {
      this.assetsService.refresh$.next(null);
    }
    this.cdRef.detectChanges();
  }

  public onAssetFormDrawerToggleChanged(_event: { type: 'maintenance' | 'compliance', value: boolean }): void {
    if (this.asset?.id) {
      this.subscriptions.push(
        this.assetsService.getAsset(this.asset.id).subscribe((asset) => {
          this.asset = asset;
          this.cdRef.detectChanges();
        })
      );
    }
  }

  public seeMoreToggle(): void {
    this.seeMore = !this.seeMore;
    this.cdRef.detectChanges();
  }

  public getLastNotationInfo(): string {
    let info = '';
    if (this.lastNotation.user?.name) {
      info = this.lastNotation.user.name
    }
    if (this.lastNotation) {
      const date = new Date(this.lastNotation.datetime);
      const formattedDate = new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
      if (info) {
        info += ', ';
      }
      info += this.translate.instant('the') + ' ' + formattedDate
    }
    return info
  }

  public onInvestmentsAttached(investmentIds: number[]): void {
    if (!this.asset?.id) return;

    // Creation and detachment are already saved by the investment drawer.
    if (investmentIds === null) {
      this.assetsService.refresh$.next(null);
      return;
    }

    // The attachment dialog allows one selection at a time.
    const investmentId = investmentIds[0];
    if (!investmentId || this.asset.investments?.some(investment => investment.id === investmentId)) return;

    this.saveToast.showSaving();
    this.subscriptions.push(this.api.investments_attach_investment({
      asset_id: this.asset.id,
      investment_id: investmentId,
    }).pipe(first()).subscribe({
      next: (result: any) => {
        if (result?.ok === false) {
          this.saveToast.showError(result.errors);
          return;
        }
        this.saveToast.showSaved();
        this.assetsService.refresh$.next(null);
        this.cdRef.detectChanges();
      },
      error: error => this.saveToast.showError(error?.error?.errors || error?.error?.message),
    }));
  }
}
